import React from "react"
import { graphql } from "gatsby"
import Layout from "../../components/shared/layoutParallax"
import SEO from "../../components/SEO/SEOPage"
import Img from "gatsby-image"
import styled from "@emotion/styled"
import {
  wrapper,
  color,
  featuredWrapper,
  noFeaturedWrapper,
  breakpoints,
  ContentStyles,
  ContentStylesExt,
} from "../../utils/style"
import Breadcrumbs from "../../components/shared/Breadcrumbs"
import IcomoonIcon from "../../components/shared/IcomoonIcon"
import GetWindowWidth from "../../components/shared/GetWindowWidth"

const WrapperDesktop = styled("div")`
  ${wrapper}
`

const FeaturedWrapper = styled("div")`
  ${featuredWrapper}
  position: relative;
  overflow: hidden;
  margin-bottom: 30px;
  .top-wrap {
    position: absolute;
    top: 25%;
    left: 32px;
    right: 32px;
  }
  h1 {
    margin: 0 0 20px;
    font-weight: 800;
    font-size: 64px;
    line-height: 124.6%;
  }
  .subtitle {
    font-size: 16px;
    line-height: 24px;
    white-space: pre-wrap;
  }
  .gatsby-image-wrapper {
    border-radius: 24px;
    max-height: 448px;
    &::after {
      content: "";
      width: 100%;
      height: 100%;
      background: ${color.transparentBlack};
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      margin: 0 auto;
      border-radius: 24px;
    }
  }
  img {
    border-radius: 24px;
  }
  @media (max-width: ${breakpoints.md}) {
    .gatsby-image-wrapper {
      max-height: 100%;
    }
    h1 {
      font-size: 26px;
      line-height: 32px;
      margin: 0 0 16px;
    }
  }
  @media (max-width: ${breakpoints.xs}) {
    .top-wrap {
      top: 30%;
      left: 16px;
      right: 16px;
    }
    h1 {
      font-size: 22px;
      line-height: 30px;
      margin: 0;
    }
  }
`

const NoFeaturedWrapper = styled("div")`
  ${noFeaturedWrapper}
`

const ContentWrapper = styled("div")`
  ${wrapper}
  ${ContentStyles}
  ${ContentStylesExt}
  padding: 0;
`

const BottomBanner = styled("div")`
  background: linear-gradient(
    128.46deg,
    rgba(169, 0, 0, 0.4) 25.23%,
    rgba(120, 0, 0, 0.1) 88.32%
  );
  padding: 32px 0;
  text-align: center;
  span {
    font-weight: 700;
    font-size: 22px;
    line-height: 32px;
    color: ${color.gold};
  }
`

const ContactWrap = styled("div")`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  .col {
    flex-basis: 32%;
    margin-right: 1%;
    background: ${color.transparentWhite1};
    border-radius: 24px;
    padding: 29px 16px 16px;
    text-align: center;
    &:last-child {
      margin-right: 0;
    }
  }
  svg {
    filter: drop-shadow(1px 1px 6px ${color.gold});
    margin-bottom: 30px;
  }
  .button {
    width: 100%;
  }
  span {
    font-weight: bold;
    font-size: 28px;
    line-height: 40px;
    color: ${color.gold};
    margin-bottom: 15px;
    display: block;
  }
  @media (max-width: ${breakpoints.md}) {
    flex-wrap: wrap;
    .col {
      flex-basis: 49%;
      margin-bottom: 2%;
      &:nth-of-type(2n) {
        margin-right: 0;
      }
    }
  }
  @media (max-width: ${breakpoints.sm}) {
    .col {
      flex-basis: 100%;
      margin-right: 0;
    }
  }
`

const BottomColumn = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  .col {
    flex-basis: 48%;
    background: ${color.transparentWhite1};
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 32px;
    margin-right: 1%;
  }
  @media (max-width: ${breakpoints.sm}) {
    .col {
      flex-basis: 100%;
      margin-right: 0;
      margin-bottom: 24px;
    }
  }
`

const showHideChat = (id, frameID) => {
  const chatBox = document.getElementById(id)
  let chatIframe = document.getElementById(frameID)
  chatBox.classList.toggle("open")
  if (chatBox.classList.contains("open")) {
    chatIframe.src = process.env.FRESHCHAT_IFRAME_SRC
  } else {
    chatIframe.src = "about:blank"
  }
}

const PageTemplate = ({ data, pageContext }) => {
  const windowWidth = GetWindowWidth()
  const isDesktop = windowWidth > 600
  const currentPage = data.wordpressPage
  const breadcrumbs = pageContext.breadcrumbs
  const page_title = pageContext.page_title
  const page_subtitle = currentPage.acf.top_subtitle
  const featuredMedia = currentPage.featured_media
  const metaTitle =
    currentPage.yoast_meta && currentPage.yoast_meta.yoast_wpseo_title
      ? currentPage.yoast_meta.yoast_wpseo_title
      : currentPage.title + "- " + process.env.OWNER
  const metaDesc =
    currentPage.yoast_meta && currentPage.yoast_meta.yoast_wpseo_metadesc
      ? currentPage.yoast_meta.yoast_wpseo_metadesc
      : ""
  const content_field_one = currentPage.acf.content_field_01
  const content_field_two = currentPage.acf.content_field_02
  const content_field_tree = currentPage.acf.content_field_03
  const content_field_four = currentPage.acf.content_field_04
  const cta_footer = currentPage.acf.cta_footer
  const socialImage = currentPage.featured_media?.localFile?.publicURL

  return (
    <Layout>
      <SEO
        title={metaTitle}
        description={metaDesc}
        pathname={currentPage.path}
        breadcrumbs={breadcrumbs}
        socialImage={socialImage}
      />
      <WrapperDesktop>
        {featuredMedia &&
        featuredMedia.localFile &&
        featuredMedia.localFile.childImageSharp &&
        featuredMedia.localFile.childImageSharp.fluid &&
        isDesktop ? (
          <FeaturedWrapper>
            <Breadcrumbs breadcrumbs={breadcrumbs} page_title={page_title} />
            <Img
              fluid={featuredMedia.localFile.childImageSharp.fluid}
              alt={
                currentPage.featured_media.alt_text
                  ? currentPage.featured_media.alt_text
                  : page_title
              }
              fadeIn={false}
              loading="eager"
            />
            <div className="top-wrap">
              <h1
                className="h1 rich-design"
                dangerouslySetInnerHTML={{ __html: page_title }}
              />
              {page_subtitle && (
                <span
                  className="subtitle"
                  dangerouslySetInnerHTML={{ __html: page_subtitle }}
                />
              )}
            </div>
          </FeaturedWrapper>
        ) : (
          <NoFeaturedWrapper>
            <Breadcrumbs breadcrumbs={breadcrumbs} page_title={page_title} />
            <h1 dangerouslySetInnerHTML={{ __html: page_title }} />
            {page_subtitle && (
              <span dangerouslySetInnerHTML={{ __html: page_subtitle }} />
            )}
          </NoFeaturedWrapper>
        )}

        {currentPage.path === "/contact/" && (
          <ContactWrap>
            <div className="col">
              <div>
                <IcomoonIcon icon="facebook2" color={color.gold} size={30} />
              </div>
              <div>
                <span>Facebook</span>
              </div>
              <div>
                <a
                  href={process.env.FACEBOOK_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button transparent-button"
                >
                  Connect via Facebook
                </a>
              </div>
            </div>
            <div className="col">
              <div>
                <IcomoonIcon icon="line" color={color.gold} size={30} />
              </div>
              <div>
                <span>{process.env.LINE_ID_TEXT}</span>
              </div>
              <div>
                <a
                  href={process.env.LINE_ID}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button transparent-button"
                >
                  Connect via Line
                </a>
              </div>
            </div>
            <div className="col">
              <div>
                <IcomoonIcon icon="chat-2" color={color.gold} size={30} />
              </div>
              <div>
                <span>แชท</span>
              </div>
              <div>
                <button
                  onClick={() => showHideChat("chat-wrapper", "chat-frame")}
                  className="button transparent-button"
                >
                  Open Chat
                </button>
              </div>
            </div>
            <div className="col">
              <div>
                <IcomoonIcon icon="call-answer" color={color.gold} size={30} />
              </div>
              <div>
                <span>02-026-9397</span>
              </div>
              <div>
                <span>02-026-9398</span>
              </div>
            </div>
          </ContactWrap>
        )}

        <ContentWrapper>
          <div dangerouslySetInnerHTML={{ __html: currentPage.content }} />
          <BottomColumn>
            {content_field_one && (
              <div className="col">
                <span dangerouslySetInnerHTML={{ __html: content_field_one }} />
              </div>
            )}
            {content_field_two && (
              <div className="col">
                <span dangerouslySetInnerHTML={{ __html: content_field_two }} />
              </div>
            )}
            {content_field_tree && (
              <div className="col">
                <span
                  dangerouslySetInnerHTML={{ __html: content_field_tree }}
                />
              </div>
            )}
            {content_field_four && (
              <div className="col">
                <span
                  dangerouslySetInnerHTML={{ __html: content_field_four }}
                />
              </div>
            )}
          </BottomColumn>
        </ContentWrapper>
      </WrapperDesktop>
      {cta_footer && (
        <BottomBanner>
          <WrapperDesktop>
            <span dangerouslySetInnerHTML={{ __html: cta_footer }} />
          </WrapperDesktop>
        </BottomBanner>
      )}
    </Layout>
  )
}

export default PageTemplate

export const pageQuery = graphql`
  query($id: String!) {
    wordpressPage(id: { eq: $id }) {
      wordpress_id
      title
      content
      date(formatString: "MMMM DD, YYYY")
      path
      yoast_meta {
        yoast_wpseo_title
        yoast_wpseo_metadesc
      }
      acf {
        page_template
        top_subtitle
        cta_footer
        content_field_01
        content_field_02
        content_field_03
        content_field_04
      }
      featured_media {
        alt_text
        localFile {
          publicURL
          childImageSharp {
            fluid(maxWidth: 1400) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }
      }
    }
  }
`
